import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Segmentos de ORIGEM do `loc` do pydantic — dizem onde o dado veio, nao qual
// campo falhou. Nao entram no texto que o usuario le.
const _LOC_ORIGENS = new Set(["body", "query", "path", "header"]);

/** Campo apontado por um erro do pydantic, ou "" quando o `loc` nao identifica
 *  um. Indices entram no caminho (`items.0.cnpj`) para nao perder QUAL item. */
function _campoDoLoc(loc) {
  if (!Array.isArray(loc) || loc.length === 0) return "";
  const segs = [...loc];
  while (segs.length && typeof segs[0] === "string" && _LOC_ORIGENS.has(segs[0])) {
    segs.shift();
  }
  return segs.filter((s) => typeof s === "string" || typeof s === "number").join(".");
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FS-089 (incidente Orielo): o 422 do pydantic carrega o campo em `loc` e a
    // causa em `msg`. Devolver so o `msg` custava as DUAS pontas — o master lia
    // "Extra inputs are not permitted" sem saber onde mexer, e o mapa PT-BR
    // (`_V2_ERROR_PT`, tokenScopes.js, que casa POR NOME DE CAMPO) nao tinha
    // como reconhecer o erro, caindo no toast generico. Prefixar o campo
    // devolve as duas.
    return detail
      .map((e) => {
        if (!e || typeof e.msg !== "string") return JSON.stringify(e);
        const campo = _campoDoLoc(e.loc);
        return campo ? `${campo}: ${e.msg}` : e.msg;
      })
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatBRL(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

/**
 * Dinheiro que pode ser DESCONHECIDO (item 15 / migration q5r6s7t8u9v0).
 *
 * `operations.valor_liquido` é NULL quando a V2 não devolveu o líquido em
 * NENHUMA das fontes. `formatBRL(null)` diria "R$ 0,00" — a Zemo AFIRMANDO que
 * vai pagar zero. Sem dado, "—" (mesmo padrão do `formatTaxaPrazo` para
 * `taxa_mensal` NULL). Só null/undefined viram traço: 0 REAL segue "R$ 0,00".
 */
export function formatBRLouTraco(value) {
  if (value === null || value === undefined) return "—";
  return formatBRL(value);
}

// Documento do cadastro — o backend grava SEMPRE em dígitos (`_clean_doc`),
// mas o auto-registro aceita CNPJ (14) e CPF (11). Máscara só quando o tamanho
// bate; qualquer outro valor sai INTACTO (não inventar formatação sobre um
// documento que não reconhecemos).
export function formatCNPJ(cnpj) {
  if (!cnpj) return "—";
  const d = String(cnpj).replace(/\D/g, "");
  if (d.length === 14) {
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }
  if (d.length === 11) {
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  return String(cnpj);
}

// Rótulo honesto do documento: 11 dígitos é CPF, o resto rotula-se CNPJ
// (o campo do parceiro aceita os dois — ver RegisterPayload.doc).
export function docLabel(doc) {
  return String(doc || "").replace(/\D/g, "").length === 11 ? "CPF" : "CNPJ";
}

export function formatDateBR(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

export function formatDateTimeBR(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
}
